import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  createTimelineEventDraft,
  type TimelineEventDraft,
} from "@/features/timeline";
import { cn } from "@/lib/utils";

type TimelineCreateEventDialogProps = {
  onCreate: (draft: TimelineEventDraft) => Promise<void>;
  onOpenChange: (open: boolean) => void;
  open: boolean;
};

const textareaClassName =
  "min-h-[10rem] w-full resize-none border-0 border-b border-border/85 bg-transparent px-0 py-3 text-base text-foreground transition-colors placeholder:text-muted-foreground/90 focus-visible:outline-none";

export function TimelineCreateEventDialog({
  onCreate,
  onOpenChange,
  open,
}: TimelineCreateEventDialogProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<TimelineEventDraft>(createTimelineEventDraft());
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setDraft(createTimelineEventDraft());
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  async function handleCreate() {
    if (submitting) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await onCreate(draft);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "timeline.messages.saveError");
      setSubmitting(false);
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="w-[min(94vw,70rem)] gap-0 overflow-hidden border-white/70 bg-[linear-gradient(180deg,#f7f0e4,#f3ebdf)] p-0">
        <DialogHeader className="border-b border-border/70 px-8 pb-6 pt-8">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-clay">
            {t("timeline.createDialog.eyebrow", { defaultValue: "New event" })}
          </p>
          <DialogTitle className="pt-2 text-[2.45rem] leading-none">
            {t("timeline.createDialog.title", {
              defaultValue: "Create timeline event",
            })}
          </DialogTitle>
          <DialogDescription className="pt-2 text-base leading-7">
            {t("timeline.createDialog.body", {
              defaultValue:
                "Capture the title, date, and why this moment matters. Illustration and linked records can be added right after creation.",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-10 px-8 py-8">
          {error ? (
            <div
              className="rounded-[1.15rem] border border-destructive/35 bg-destructive/5 px-4 py-3 text-sm text-destructive"
              role="alert"
            >
              {t(error, { defaultValue: error })}
            </div>
          ) : null}

          <div className="grid gap-8 md:grid-cols-2">
            <div className="space-y-3">
              <Label className="text-[1.05rem] font-semibold text-foreground" htmlFor="timeline-create-title">
                {t("timeline.fields.title")}
              </Label>
              <Input
                className="h-auto rounded-none border-0 border-b border-border/85 bg-transparent px-0 py-3 text-[1.1rem] shadow-none focus-visible:ring-0"
                id="timeline-create-title"
                onChange={(event) =>
                  setDraft((current) => ({ ...current, title: event.target.value }))
                }
                placeholder={t("timeline.createDialog.titlePlaceholder", {
                  defaultValue: "For example: The Great Alliance",
                })}
                value={draft.title}
              />
            </div>

            <div className="space-y-3">
              <Label className="text-[1.05rem] font-semibold text-foreground" htmlFor="timeline-create-era">
                {t("timeline.fields.eraLabel")}
              </Label>
              <Input
                className="h-auto rounded-none border-0 border-b border-border/85 bg-transparent px-0 py-3 text-[1.1rem] shadow-none focus-visible:ring-0"
                id="timeline-create-era"
                onChange={(event) =>
                  setDraft((current) => ({ ...current, eraLabel: event.target.value }))
                }
                placeholder={t("timeline.createDialog.eraPlaceholder", {
                  defaultValue: "For example: March 15, 1950",
                })}
                value={draft.eraLabel}
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-[1.05rem] font-semibold text-foreground" htmlFor="timeline-create-description">
              {t("timeline.fields.description")}
            </Label>
            <textarea
              className={cn(textareaClassName)}
              id="timeline-create-description"
              onChange={(event) =>
                setDraft((current) => ({ ...current, description: event.target.value }))
              }
              placeholder={t("timeline.createDialog.descriptionPlaceholder", {
                defaultValue:
                  "Describe what happened in this event and why it matters inside the chronology...",
              })}
              value={draft.description}
            />
          </div>

          <div className="rounded-[1.35rem] border border-dashed border-border/75 bg-secondary/25 px-5 py-4 text-sm leading-7 text-muted-foreground">
            {t("timeline.createDialog.followup", {
              defaultValue:
                "After creating the event, you can attach an illustration, generate an image, and link records from the editor panel.",
            })}
          </div>
        </div>

        <DialogFooter className="border-t border-border/70 px-8 py-6 sm:justify-between">
          <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
            {t("common.cancel")}
          </Button>
          <Button onClick={() => void handleCreate()} type="button">
            {submitting
              ? t("timeline.actions.saving")
              : t("timeline.createDialog.confirm", { defaultValue: "Create event" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
